/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import { ReactNode, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  name: yup.string(),
  subject: yup.string(),
  email: yup.string(),
  message: yup.string(),
  phoneNumber: yup.string(),
  createdAt: yup.string()
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormContactMessageHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormContactMessageHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormContactMessage is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormContactMessage from './src/views/PostEdit/FormContactMessage.tsx'
 *
 * const MyComponent = () => (
 *   <FormContactMessage />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormContactMessage: FC<Props> = ({
  onSubmit = () => {},
  initialValues = {},
  isLoading = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Assunto</Form.Label>
          <Form.Control
            type='text'
            placeholder='Assunto'
            name='subject'
            value={formik.values.subject}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Form.Group as={Col} md={8} className="mb-3">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type='text'
            placeholder='Assunto'
            name='name'
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Número de telefone</Form.Label>
          <Form.Control
            type='text'
            placeholder='Número de telefone'
            name='phoneNumber'
            value={formik.values.phoneNumber?.replace(/(\d{2})(\d{2})(\d{4,5})(\d{4})/, "+$1 ($2) $3-$4")}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Endereço de email</Form.Label>
          <Form.Control
            type='text'
            placeholder='Endereço de email'
            name='email'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <Form.Label>Data de recebimento</Form.Label>
          <Form.Control
            type='text'
            placeholder='Assunto'
            name='createdAt'
            value={formik.values.createdAt}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Mensagem</Form.Label>
          <Form.Control
            type='text'
            as='textarea'
            rows={10}
            placeholder='Mensagem'
            name='message'
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Col md={12}>
          <Button
            variant="link"
            style={{ marginRight: 10 }}
            onClick={() => navigate('/contact-messages')}
          >
            Voltar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormContactMessage
